import { type FC, useState } from 'react'
import classNames from 'classnames'
import { Button } from '../../../../../../components/Button/Button'
import { type IntegrationWorkflow, type IntegrationWorkflowCapabilities, type IntegrationWorkflowStep } from '../../../../../../models/IntegrationWorkflow'
import { CompleteStepForm } from './CompleteStepForm'
import { type CompleteIntegrationWorkflowStepInput, getVisibleWorkflowSteps, type IntegrationWorkflowTarget } from './integrationWorkflow'
import { useCompleteIntegrationWorkflowStep } from './useIntegrationWorkflowActions'
import styles from './WorkflowStepTimeline.module.scss'

interface WorkflowStepTimelineProps {
  target: IntegrationWorkflowTarget
  workflow: IntegrationWorkflow
  capabilities: IntegrationWorkflowCapabilities
  onStepCompleted?: (workflowId: number) => void
}

export const WorkflowStepTimeline: FC<WorkflowStepTimelineProps> = ({ target, workflow, capabilities, onStepCompleted }) => {
  const [formStepId, setFormStepId] = useState<number | null>(null)
  const [complete, { isLoading: isSubmitting, isError, reset }] = useCompleteIntegrationWorkflowStep(target)

  const { activeStep } = workflow
  const needsForm = Boolean(activeStep?.outcomes.length) || capabilities.stepNote
  const isFormOpen = activeStep !== null && formStepId === activeStep.id

  const completeStep = async (step: IntegrationWorkflowStep, input: CompleteIntegrationWorkflowStepInput) => {
    const result = await complete(workflow.id, step.id, input)
    if (result.data) {
      onStepCompleted?.(workflow.id)
    }
  }

  const closeForm = () => {
    setFormStepId(null)
    reset()
  }

  return (
    <ol className='list-unstyled mb-0 zocks-ps-2.25 pe-4 pb-2' aria-label={`${workflow.name ?? 'Workflow'} steps`}>
      {getVisibleWorkflowSteps(workflow).map((step, index) => {
        const isCurrent = step.id === activeStep?.id
        return (
          <li
            key={step.id}
            className={classNames(styles.step, 'd-flex gap-3 pb-3', { [styles.done]: !isCurrent })}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <span
              className={classNames(
                styles.badge,
                'd-flex align-items-center justify-content-center flex-shrink-0 rounded-circle border border-2 paragraph-extra-small bold',
                isCurrent ? 'zocks-bg-color-white zocks-border-color-primary zocks-text-primary' : 'bg-success border-success text-white',
              )}
            >
              {isCurrent ? index + 1 : <i className='bi bi-check-lg' role='img' aria-label='Completed' />}
            </span>
            <div className='d-flex flex-column flex-grow-1'>
              <div className='d-flex align-items-start justify-content-between gap-3'>
                <div className='d-flex flex-column'>
                  <span className='paragraph-small medium'>{step.name}</span>
                  {isCurrent ? null : <span className='paragraph-extra-small zocks-text-gray-600 pt-1'>Completed</span>}
                </div>
                {isCurrent && isFormOpen ? (
                  <Button variant='borderless' className='button-small' aria-label='Cancel' disabled={isSubmitting} onClick={closeForm}>
                    <i className='bi bi-x-lg' />
                  </Button>
                ) : null}
                {isCurrent && !isFormOpen ? (
                  <Button
                    variant='secondary'
                    className='button-small text-nowrap'
                    loading={isSubmitting}
                    onClick={() => (needsForm ? setFormStepId(step.id) : completeStep(step, {}))}
                  >
                    <i className='bi bi-check2' />
                    Mark step as completed
                  </Button>
                ) : null}
              </div>
              {isCurrent && isFormOpen ? (
                <CompleteStepForm step={step} showNote={capabilities.stepNote} isSubmitting={isSubmitting} onComplete={(input) => completeStep(step, input)} />
              ) : null}
              {isCurrent && isError ? (
                <span className='paragraph-extra-small zocks-text-danger pt-2'>We could not complete this step. Please try again.</span>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
